/*
 * Copyright 2026 Sony Corporation
 */
import type { Device, Project } from '@ips-jit/schema';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { CARD_GRID, NewCard } from '../components/NewCard';
import { PLATFORM_LABEL, PlatformIcon } from '../components/platform';
import { ProjectThumbnail } from '../components/ProjectThumbnail';
import { useAuth } from '../contexts/auth-context';

const describe = (error: unknown, fallback: string): string => (error instanceof Error ? error.message : fallback);

/** Choose the runtime first, then name the project. The runtime cannot be changed afterwards. */
const NewProjectDialog = ({
  open,
  runtimes,
  busy,
  onClose,
  onCreate,
}: {
  open: boolean;
  runtimes: Device[] | undefined;
  busy: boolean;
  onClose: () => void;
  onCreate: (body: { name: string; runtime_id: string }) => void;
}) => {
  const [runtimeId, setRuntimeId] = useState('');
  const [name, setName] = useState('');

  useEffect(() => {
    if (open) {
      setRuntimeId(runtimes?.length === 1 ? runtimes[0].device_id : '');
      setName('');
    }
  }, [open, runtimes]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    onCreate({ name: name.trim(), runtime_id: runtimeId });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <Box component="form" onSubmit={submit}>
        <DialogTitle>New project</DialogTitle>
        <DialogContent>
          {runtimes === undefined ? (
            <Typography color="text.secondary">Loading runtimes...</Typography>
          ) : runtimes.length === 0 ? (
            <DialogContentText>
              A project builds an app for one runtime, the PC that runs it. Create a runtime first.
            </DialogContentText>
          ) : (
            <Stack spacing={2}>
              <DialogContentText>
                Choose the runtime the app is for. It cannot be changed once the project is created.
              </DialogContentText>
              <List aria-label="Runtime" disablePadding sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
                {runtimes.map((runtime) => (
                  <ListItemButton
                    key={runtime.device_id}
                    selected={runtime.device_id === runtimeId}
                    onClick={() => setRuntimeId(runtime.device_id)}
                    aria-pressed={runtime.device_id === runtimeId}
                  >
                    <ListItemIcon>
                      <PlatformIcon platform={runtime.platform} />
                    </ListItemIcon>
                    <ListItemText
                      primary={runtime.name}
                      secondary={[PLATFORM_LABEL[runtime.platform], runtime.camera_models.join(', ')]
                        .filter(Boolean)
                        .join(' · ')}
                    />
                    <Chip
                      size="small"
                      label={runtime.status}
                      color={runtime.status === 'online' ? 'success' : 'default'}
                    />
                  </ListItemButton>
                ))}
              </List>
              <TextField
                label="Project name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                slotProps={{ htmlInput: { maxLength: 80 } }}
                disabled={runtimeId === ''}
                required
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          {runtimes?.length === 0 ? (
            <Button component={RouterLink} to="/runtimes" variant="contained">
              Go to runtimes
            </Button>
          ) : (
            <Button type="submit" variant="contained" disabled={busy || runtimeId === '' || !name.trim()}>
              Create project
            </Button>
          )}
        </DialogActions>
      </Box>
    </Dialog>
  );
};

const ProjectCard = ({ project, runtime }: { project: Project; runtime: Device | undefined }) => {
  const navigate = useNavigate();
  const runtimeText = project.runtime_id === null ? 'No runtime' : (runtime?.name ?? 'Runtime deleted');
  return (
    <Card variant="outlined" component="article" aria-label={`Project ${project.name}`}>
      <CardActionArea onClick={() => navigate(`/projects/${project.project_id}`)} sx={{ height: '100%' }}>
        <ProjectThumbnail projectId={project.project_id} title={project.name} />
        <CardContent>
          <Typography variant="h6" component="h2" noWrap title={project.name}>
            {project.name}
          </Typography>
          <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', color: 'text.secondary' }}>
            <PlatformIcon platform={project.platform} sx={{ fontSize: 16 }} />
            <Typography variant="body2" noWrap>
              {runtimeText}
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {project.current_version > 0 ? `version ${project.current_version}` : 'not built yet'} · created{' '}
            {new Date(project.created_at).toLocaleDateString()}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

/** The user's projects as cards with the app's screen, after the card that makes a new one. */
export const Projects = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>();
  const [runtimes, setRuntimes] = useState<Device[]>();
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setProjects(await api.listProjects());
    } catch (caught) {
      setError(describe(caught, 'Could not load projects.'));
    }
  }, [api]);

  useEffect(() => {
    void load();
    api.listDevices().then(setRuntimes, () => setRuntimes([]));
  }, [api, load]);

  const create = async (body: { name: string; runtime_id: string }) => {
    setBusy(true);
    setError(undefined);
    try {
      const project = await api.createProject(body);
      navigate(`/projects/${project.project_id}`);
    } catch (caught) {
      setError(describe(caught, 'Could not create the project.'));
      setCreating(false);
      setBusy(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Typography variant="h5">Projects</Typography>
      {error && <Alert severity="error">{error}</Alert>}
      {projects === undefined ? (
        <Typography color="text.secondary">Loading...</Typography>
      ) : (
        <>
          <Box sx={CARD_GRID}>
            <NewCard label="New project" onClick={() => setCreating(true)} />
            {projects.map((project) => (
              <ProjectCard
                key={project.project_id}
                project={project}
                runtime={runtimes?.find((r) => r.device_id === project.runtime_id)}
              />
            ))}
          </Box>
          {projects.length === 0 && (
            <Typography color="text.secondary">No projects yet. Create one to describe the app you want.</Typography>
          )}
        </>
      )}
      <NewProjectDialog
        open={creating}
        runtimes={runtimes}
        busy={busy}
        onClose={() => setCreating(false)}
        onCreate={(body) => void create(body)}
      />
    </Stack>
  );
};
